/**
 * AccessCodeService — mirrors ConneXt v2 AccessByCodes + AccessCodeTemplate.
 * Handles promo/access code entry, validation, and storage.
 * Template HTML comes from the API response (Settings.CodeSetTemplates).
 */
import { HttpService } from '@Engage.Data/services/http/HttpService';
import { CookiesStorageService } from '@Engage.Data/services';
import { StorageKeys } from '@Engage.Data/constants/Constants';
import { AppOptions } from '../../../application/options/AppOptions';
import { logService, eventsService, AppEvents } from '@Engage.Core';
export class AccessCodeService {
    /** Store code set templates from the paywall response */
    static setCodeSetTemplates(templates) {
        if (Array.isArray(templates)) {
            this.codeSetTemplates = templates.map(t => {
                var _a, _b, _c, _d, _e, _f;
                return ({
                    id: (_b = (_a = t.id) !== null && _a !== void 0 ? _a : t.Id) !== null && _b !== void 0 ? _b : 0,
                    name: (_d = (_c = t.name) !== null && _c !== void 0 ? _c : t.Name) !== null && _d !== void 0 ? _d : '',
                    html: (_f = (_e = t.html) !== null && _e !== void 0 ? _e : t.Html) !== null && _f !== void 0 ? _f : ''
                });
            });
        }
    }
    static hasSavedCode() {
        return !!this.getSavedCode();
    }
    static getSavedCode() {
        try {
            const raw = CookiesStorageService.get(StorageKeys.AllowUseCode, { isSecureValue: true });
            if (!raw)
                return null;
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            return { Id: parsed.Id, Expiration: parsed.Exp || parsed.Expiration };
        }
        catch (_a) {
            return null;
        }
    }
    static async checkCode(inputCode, codeListName) {
        var _a;
        if (!inputCode)
            return false;
        try {
            const payload = {
                Code: inputCode,
                CodeListSetName: codeListName || '',
                ClientCode: ((_a = AppOptions.getClientCode) === null || _a === void 0 ? void 0 : _a.call(AppOptions)) || '',
                SiteCode: AppOptions.getSiteCode() || ''
            };
            const response = await HttpService.post('accesscode/check', payload);
            if ((response === null || response === void 0 ? void 0 : response.Id) && (response === null || response === void 0 ? void 0 : response.Expiration)) {
                logService.log('[AccessCode] Code validated', `Id=${response.Id}`);
                this.saveCode(response.Id, response.Expiration);
                eventsService.dispatch(AppEvents.AccessGranted, { Name: codeListName || 'AccessCode', Id: response.Id });
                return true;
            }
            eventsService.dispatch(AppEvents.AccessDenied, { reason: 'Invalid access code' });
            return false;
        }
        catch (err) {
            logService.warn('[AccessCode] Check failed', (err === null || err === void 0 ? void 0 : err.ErrorMessage) || err);
            eventsService.dispatch(AppEvents.AccessDenied, { reason: 'Invalid access code' });
            return false;
        }
    }
    static saveCode(id, expiration) {
        CookiesStorageService.set(StorageKeys.AllowUseCode, JSON.stringify({ Id: id, Exp: Date.parse(expiration) }), {
            isSecureValue: true,
            expires: new Date(expiration)
        });
    }
    static removeCode() {
        CookiesStorageService.delete(StorageKeys.AllowUseCode);
    }
    static bindAccessCodeButtons() {
        document.addEventListener('click', (e) => {
            const el = e.target;
            const target = el === null || el === void 0 ? void 0 : el.closest('[data-mg2-action="accessCode"], [data-mg2-action="access code"]');
            if (!target)
                return;
            e.preventDefault();
            const codeListId = target.getAttribute('data-mg2-code-set') || target.getAttribute('attrlist') || '';
            this.showAccessCodePrompt(codeListId);
        });
    }
    /**
     * Shows the access code modal using the template HTML from the API.
     * Falls back to a default template if none was provided.
     */
    static showAccessCodePrompt(codeListId) {
        var _a, _b;
        const template = this.codeSetTemplates.find(t => String(t.id) === String(codeListId));
        const templateHtml = (template === null || template === void 0 ? void 0 : template.html) || this.getDefaultTemplate();
        (_a = document.getElementById('engage-access-code-modal')) === null || _a === void 0 ? void 0 : _a.remove();
        (_b = document.querySelector('.engage-access-code-backdrop')) === null || _b === void 0 ? void 0 : _b.remove();
        // Backdrop — exactly mirrors ConneXt v2: div.connext-modal-backdrop.fade
        const backdrop = document.createElement('div');
        backdrop.className = 'connext-modal-backdrop fade engage-access-code-backdrop';
        backdrop.style.zIndex = '10049';
        document.body.appendChild(backdrop);
        // Container — exactly mirrors ConneXt v2 BSModal.createContainer(): div.modal-scrollable
        const container = document.createElement('div');
        container.id = 'engage-access-code-modal';
        container.className = 'modal-scrollable';
        container.style.zIndex = '10050';
        document.body.appendChild(container);
        // Append template HTML inside the container (mirrors BSModal constructor)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = templateHtml;
        // Strip inline style attributes from editable elements so CSS classes take effect
        // (Template editor adds inline styles for preview; in production, CSS classes should control appearance)
        tempDiv.querySelectorAll('[style]').forEach(el => {
            // Preserve display:none (used for message elements)
            const display = el.style.display;
            el.removeAttribute('style');
            if (display === 'none') {
                el.style.display = 'none';
            }
        });
        const modalElement = tempDiv.firstElementChild || tempDiv;
        container.appendChild(tempDiv.childNodes.length === 1 ? modalElement : tempDiv);
        // Show the modal — mirrors BSModal.show()
        const modal = container.querySelector('.modal, .Mg2-connext, [data-display-type]');
        if (modal) {
            modal.style.display = 'block';
            modal.classList.add('in');
            modal.setAttribute('aria-hidden', 'false');
        }
        // Fix Font Awesome icons (mirrors ConneXt v2 Template.fixFontAwesomeStyle)
        // Replaces .fa class with inline styles since FA isn't loaded
        container.querySelectorAll('.fa').forEach(el => {
            var _a;
            el.style.display = el.style.display || 'inline-block';
            el.style.font = 'normal normal normal 14px/1 FontAwesome';
            el.style.fontSize = 'inherit';
            el.style.textRendering = 'auto';
            el.classList.remove('fa');
            el.classList.add('cb');
            // For fa-times (×), inject content directly if empty
            if (el.classList.contains('fa-times') && !((_a = el.textContent) === null || _a === void 0 ? void 0 : _a.trim())) {
                el.textContent = '×';
                el.style.fontFamily = 'Arial, sans-serif';
                el.style.fontSize = '20px';
                el.style.lineHeight = '1';
            }
        });
        // Prevent page scroll — mirrors BsModalService.resetPageScroll()
        document.body.classList.add('modal-open');
        document.documentElement.classList.add('modal-open');
        eventsService.dispatch(AppEvents.AccessTemplateShown, null);
        const closeModal = () => {
            container.remove();
            backdrop.remove();
            document.body.classList.remove('modal-open');
            document.documentElement.classList.remove('modal-open');
            eventsService.dispatch(AppEvents.AccessTemplateClosed, null);
        };
        // Close on dismiss button — mirrors ConneXt v2 Template.addCloseBtnListener()
        container.querySelectorAll('[data-dismiss="modal"], [data-dismiss="info-box"]').forEach(btn => {
            btn.addEventListener('click', (e) => { e.preventDefault(); closeModal(); });
        });
        // Close on clicking outside the modal (on the container/scrollable area)
        container.addEventListener('mousedown', (e) => {
            if (!e.target.closest('.modal, .Mg2-connext, [data-display-type]')) {
                closeModal();
            }
        });
        // ESC key close
        const escHandler = (e) => {
            if (e.key === 'Escape' || e.keyCode === 27) {
                document.removeEventListener('keydown', escHandler);
                closeModal();
            }
        };
        document.addEventListener('keydown', escHandler);
        // Submit button
        const submitBtn = container.querySelector('[data-mg2-action="click"], .Mg2-pin__button');
        const input = container.querySelector('.Mg2-pin__input, input[type="password"], input[type="text"]');
        const messageEl = container.querySelector('.Mg2-pin__message');
        if (submitBtn && input) {
            submitBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const code = input.value.trim();
                if (messageEl)
                    messageEl.style.display = 'none';
                if (!code) {
                    if (messageEl) {
                        messageEl.textContent = 'Please enter a code';
                        messageEl.className = 'Mg2-pin__message Mg2-pin__message_error';
                        messageEl.style.display = '';
                    }
                    return;
                }
                submitBtn.setAttribute('disabled', 'true');
                submitBtn.style.pointerEvents = 'none';
                this.checkCode(code, (template === null || template === void 0 ? void 0 : template.name) || codeListId).then(valid => {
                    if (valid) {
                        if (messageEl) {
                            messageEl.textContent = 'Success! Now you have full access';
                            messageEl.className = 'Mg2-pin__message Mg2-pin__message_success';
                            messageEl.style.display = '';
                        }
                        setTimeout(() => { closeModal(); window.location.reload(); }, 1000);
                    }
                    else {
                        if (messageEl) {
                            messageEl.textContent = 'Invalid access code. Please try again.';
                            messageEl.className = 'Mg2-pin__message Mg2-pin__message_error';
                            messageEl.style.display = '';
                        }
                        input.value = '';
                        submitBtn.removeAttribute('disabled');
                        submitBtn.style.pointerEvents = '';
                    }
                });
            });
            input.addEventListener('keydown', (e) => { if (e.key === 'Enter')
                submitBtn.click(); });
            input.focus();
        }
    }
    static getDefaultTemplate() {
        return `<div data-display-type="modal" tabindex="-1" data-width="380" class="Mg2-connext modal Mg2-pin-modal fade in" style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:100000011;width:380px;">
            <div class="modal-body m-0 p-0 Mg2-pin x-editable-css-bg x-editable-text" style="background:#fff;border-radius:8px;padding:24px;box-shadow:0 4px 20px rgba(0,0,0,0.2);">
                <a href="#" class="fa fa-times closebtn Mg2-close-inline" data-dismiss="modal" aria-label="Close" aria-hidden="true" style="position:absolute;top:10px;right:14px;font-size:20px;text-decoration:none;color:#333;">&times;</a>
                <div class="m-0 p-0 Mg2-pin__title" style="font-size:18px;font-weight:bold;margin-bottom:8px;">Please enter your PIN</div>
                <div class="m-0 p-0 Mg2-pin__subtitle" style="font-size:13px;color:#666;margin-bottom:16px;">If you have been given an access PIN, please input it in the box below.</div>
                <div class="Mg2-pin__message" style="display:none;padding:8px;margin-bottom:10px;border-radius:4px;font-size:13px;"></div>
                <div class="m-0 p-0 Mg2-pin__input-row">
                    <input type="password" class="Mg2-pin__input" placeholder="Enter access code" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:4px;font-size:14px;box-sizing:border-box;margin-bottom:12px;" />
                </div>
                <div class="Mg2-pin__button-row" style="text-align:center;">
                    <a class="Mg2-pin__button" data-mg2-action="click" style="display:inline-block;padding:10px 24px;background:#286deb;color:#fff;text-decoration:none;border-radius:4px;font-size:14px;cursor:pointer;">Submit</a>
                </div>
            </div>
        </div>`;
    }
}
/** Cached code set templates from API response */
AccessCodeService.codeSetTemplates = [];
